import { Component, computed, inject, OnDestroy, OnInit, Signal, ChangeDetectionStrategy } from '@angular/core';

import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  RemoteConfigStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_REMOTE_CONFIG } from '@softline/application';
import { Store } from '@softline/core';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR } from '@softline/ui-core';
import { BehaviorSubject, filter, map, of, Subscription, switchMap } from 'rxjs';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../../../kommissionierung.shared';
import { RemoteConfigService } from '../../../../services/remote-config.service';
import { LlrBestellungStore } from '../../../../store/bestellung.store';
import { LlrEmballageStore } from '../../../../store/emballage.store';
import { LlrKommissionierungStore } from '../../../../store/kommissionierung.store';
import { LlrPalleteStore } from '../../../../store/palette.store';
import {
  Kommissionierung,
  KommissionierungBewegung } from '../../../../types/kommissionierung';
import { Palette } from '../../../../types/palette';
import { DateValidator } from '../../../../validators/dateValidator';

import { DateInputComponent } from '@softline/ui-core/date-input';
import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-mhd-charge-erfassung',
  imports: [ReactiveFormsModule,
    DateInputComponent,
    InputComponent,
    TranslatePipe],
  templateUrl: './mhd-charge-erfassung.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mhd-charge-erfassung.component.css'] })
export class MhdChargeErfassungComponent implements OnInit, OnDestroy, BackNavigable {
  private store = inject(Store);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private remoteConfig = inject(RemoteConfigService);
  private backNavigationService = inject(BackNavigationService);
  private kommissionierungSubscription?: Subscription;
  private formValueChangesSubscription?: Subscription;
  private kommissionierung?: Kommissionierung;
  private emballagen?: KommissionierungBewegung[];
  private emballagenGeladen!: (emballagen: KommissionierungBewegung[]) => void;

  private warteBisEmballagenGeladen = new Promise((resolve) => {
    this.emballagenGeladen = resolve;
  });

  readonly chargeEditable = toSignal(
    this.store
      .observe(SOFTLINE_FEATURE_REMOTE_CONFIG, RemoteConfigStore.getters.data)
      .pipe(
        filter((o) => !!o && !!o?.modules),
        map((data) => data?.modules?.find((o) => o.module === 'llr-bskomm')),
        map((module) => module?.properties?.find((o) => o?.key === 'CHARGE_EDITABLE')),
        map((config) => config?.value === 'true')
      ),
    {
      initialValue: true }
  );

  readonly chargeReadonly = computed(() => {
    return (
      (!this.isNeuePalette() &&
        (this.palette()?.palettearten[0]?.charge?.charge?.length ?? 0) > 0) ||
      !this.chargeEditable()
    );
  });

  readonly mhdEditable = toSignal(
    this.store
      .observe(SOFTLINE_FEATURE_REMOTE_CONFIG, RemoteConfigStore.getters.data)
      .pipe(
        filter((o) => !!o && !!o?.modules),
        map((data) => data?.modules?.find((o) => o.module === 'llr-bskomm')),
        map((module) => module?.properties?.find((o) => o?.key === 'MHD_EDITABLE')),
        map((config) => config?.value === 'true')
      ),
    {
      initialValue: true }
  );

  readonly mhdReadonly = computed(() => {
    return (
      (!this.isNeuePalette() &&
        (this.palette()?.palettearten[0]?.mhd?.length ?? 0) > 0) ||
      !this.mhdEditable()
    );
  });

  readonly emballagenEingabeNachJederKommissionierung = toSignal(
    this.store
      .observe(SOFTLINE_FEATURE_REMOTE_CONFIG, RemoteConfigStore.getters.data)
      .pipe(
        filter((o) => !!o && !!o?.modules),
        map((data) => data?.modules?.find((o) => o.module === 'llr-bskomm')),
        map((module) =>
          module?.properties?.find(
            (o) => o?.key === 'EMBALLAGEN_EINGABE_NACH_JEDER_KOMMISSIONIERUNG'
          )
        ),
        map((config) => config?.value === 'true')
      ),
    {
      initialValue: false }
  );

  readonly palettenId$ = this.activatedRoute.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get('paletteId') ?? null)
  );

  readonly palette: Signal<Palette | null | undefined> = toSignal(
    this.palettenId$.pipe(
      switchMap((palletenId) => {
        if (palletenId !== null && palletenId?.trim() !== '') {
          return this.store.observe(
            SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
            LlrPalleteStore.getters.entity,
            palletenId
          );
        } else {
          return of(null);
        }
      })
    )
  );

  readonly isNeuePalette = toSignal(
    this.palettenId$.pipe(
      map((palettenId) => palettenId === null || palettenId?.trim() === '')
    )
  );

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            return komm?.bewebskomms[index];
          })
        )
      )
    ),
    { initialValue: null }
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrBestellungStore.getters.loading
  );

  readonly canExecuteWeiterButton$ = new BehaviorSubject<boolean>(false);

  form = new FormGroup({
    mhd: new FormControl<string | null>(null, [
      Validators.required,
      DateValidator.GreaterThanToday]),
    charge: new FormControl<string | null>(null, [Validators.required]) });

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: MhdChargeErfassungComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_WEITER',
          class: 'menu action-menu action-menu-top',
          canExecute: this.canExecuteWeiterButton$.asObservable(),
          icon: 'fa-regular fa-forward-step',
          execute: async () => await this.mhdChargeAbschliessen() },
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_WEITER',
          class: 'menu bottom-menu primary',
          canExecute: this.canExecuteWeiterButton$.asObservable(),
          execute: async () => await this.mhdChargeAbschliessen() }] });
    const isNeuePalette = this.isNeuePalette();

    this.kommissionierungSubscription = this.kommissionierung$.subscribe((komm) => {
      this.kommissionierung = komm;
    });

    this.canExecuteWeiterButton$.next(this.form.valid);

    this.formValueChangesSubscription = this.form.valueChanges.subscribe(() => {
      this.canExecuteWeiterButton$.next(this.form.valid);
    });

    if (isNeuePalette === false) {
      const palette = this.palette();
      if (palette) {
        this.form.patchValue({
          mhd: palette.palettearten[0].mhd,
          charge: palette.palettearten[0].charge.charge });
        this.canExecuteWeiterButton$.next(this.form.valid);
      } else {
        this.canExecuteWeiterButton$.next(false);
      }
    }

    if (!this.bewegung()?.kommissionierDaten?.mhdPflicht)
      this.form.controls['mhd'].removeValidators(Validators.required);

    if (!this.bewegung()?.kommissionierDaten?.chargePflicht)
      this.form.controls['charge'].clearValidators();

    this.form.updateValueAndValidity();

    if (
      !this.bewegung()?.kommissionierDaten?.mhdPflicht &&
      !this.bewegung()?.kommissionierDaten?.chargePflicht
    ) {
      this.canExecuteWeiterButton$.next(true);
    } else {
      this.canExecuteWeiterButton$.next(this.form.valid);
    }

    try {
      const emballagen = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
        LlrEmballageStore.actions.loadMany,
        {
          pathParams: {
            idkopfbskomm: this.kommissionierung?.id ?? '' },
          clear: true }
      );

      this.emballagen = emballagen;
      this.emballagenGeladen(emballagen);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  ngOnDestroy(): void {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      MhdChargeErfassungComponent
    );

    if (this.kommissionierungSubscription && !this.kommissionierungSubscription.closed)
      this.kommissionierungSubscription.unsubscribe();

    if (this.formValueChangesSubscription && !this.formValueChangesSubscription.closed)
      this.formValueChangesSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    const palettenVorhanden =
      this.activatedRoute.snapshot.queryParamMap.get('palettenVorhanden');

    await this.router.navigate(
      [`llr-bskomm/bewegung/${this.bewegung()?.id}/artikeldaten-erfassung`],
      {
        queryParams: {
          paletteId: this.palette()?.id ?? '',
          palettenVorhanden: palettenVorhanden ?? '' } }
    );
  }

  private async mhdChargeAbschliessen() {
    const bewegung = this.bewegung();

    if (!bewegung) return;

    await this.warteBisEmballagenGeladen;

    bewegung.kommissionierDaten.mhd = this.form.controls['mhd'].value ?? '';
    if (bewegung.kommissionierDaten?.charge)
      bewegung.kommissionierDaten.charge.charge =
        this.form.controls['charge'].value ?? '';

    if (!this.kommissionierung?.bewebskomms || !bewegung) return;

    if (
      bewegung.kommissionierDaten.menge <= 0 &&
      bewegung.kommissionierDaten.offene_menge > 0
    ) {
      const palette = this.palette();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          title: '#LLR_KOMMISSIONIERUNG.MESSAGES.TITEL_MENGE_ZU_GERING',
          message: '#LLR_KOMMISSIONIERUNG.MESSAGES.LABEL_MENGE_ZU_GERING' }
      );
      await this.router.navigate(
        [`llr-bskomm/bewegung/${bewegung.id}/artikeldaten-erfassung`],
        {
          queryParams: {
            paletteId: palette?.id ?? '' } }
      );
      return;
    }

    const newBewe = await this.kommissionierungSchreiben(bewegung);

    const ausstaendigeArtikel = this.kommissionierung.bewebskomms.find(
      (o) => o.id !== bewegung.id && !o.kommissionierDaten.abgeschlossen
    );
    if (!ausstaendigeArtikel) {
      if (
        (this.emballagen ? this.emballagen.length : 0) === 0 ||
        !this.remoteConfig.config.emballagenEingabeLaden
      ) {
        const result = await this.beweAbschliessen(bewegung);

        if (!result) return;

        const bewe = result.bewebskomms.find((o) => o.id === bewegung.id);

        if (!bewe) return;

        if (bewe.kommissionierDaten.menge !== 0) {
          await this.router.navigate([
            `llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`]);
          return;
        }
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.success,
          {
            title:
              '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.TITEL_ALLE_ARTIKEL_ABGESCHLOSSEN',
            message:
              '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.LABEL_ALLE_ARTIKEL_ABGESCHLOSSEN' }
        );
        await this.router.navigate(['/llr-bskomm/warenzugang-abschliessen']);
      } else {
        await this.router.navigate(
          [`llr-bskomm/bewegung/${bewegung.id}/emballagen-erfassung`],
          {
            queryParams: { paletteId: this.palette()?.id ?? '' } }
        );
        return;
      }
    } else {
      if (!newBewe) return;

      if (
        this.remoteConfig.config.emballagenEingabeLaden &&
        this.emballagenEingabeNachJederKommissionierung() &&
        (this.emballagen ? this.emballagen.length : 0) > 0
      ) {
        await this.router.navigate(
          [`llr-bskomm/bewegung/${bewegung.id}/emballagen-erfassung`],
          {
            queryParams: { paletteId: this.palette()?.id ?? '' } }
        );
        return;
      } else {
        try {
          if (
            (this.emballagen ? this.emballagen.length : 0) === 0 ||
            !this.remoteConfig.config.emballagenEingabeLaden
          ) {
            if (ausstaendigeArtikel) {
              const beweAbschliessenResult = await this.beweAbschliessen(bewegung);

              if (!beweAbschliessenResult) return;

              const bewe = beweAbschliessenResult.bewebskomms.find(
                (o) => o.id === bewegung.id
              );

              if (!bewe) return;

              if (bewe.kommissionierDaten.menge !== 0) {
                await this.router.navigate([
                  `llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`]);
                return;
              }

              if (
                beweAbschliessenResult.bewebskomms.some(
                  (bewe) => !bewe.kommissionierDaten.abgeschlossen
                )
              ) {
                try {
                  await this.store.dispatch(
                    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
                    LlrKommissionierungStore.actions.artikelPositionAbbrechen,
                    {
                      idkopfbskomm: this.kommissionierung.id + '',
                      idbewebskomm: bewegung.id + '',
                      bewe: bewegung }
                  );
                  if (
                    beweAbschliessenResult.bewebskomms.some(
                      (bewe) =>
                        bewe.id === bewegung.id && bewe.kommissionierDaten.abgeschlossen
                    )
                  ) {
                    await this.store.dispatch(
                      SOFTLINE_FEATURE_MESSAGE_BAR,
                      MessageBarStore.actions.success,
                      {
                        title:
                          '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.TITEL_ARTIKEL_ABGESCHLOSSEN',
                        message:
                          '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.LABEL_ARTIKEL_ABGESCHLOSSEN' }
                    );
                  }
                  await this.router.navigate(['llr-bskomm/artikelauswahl']);
                  return;
                } catch (e) {
                  showRequestErrors(this.store, e);
                }
              }
            }
            await this.store.dispatch(
              SOFTLINE_FEATURE_MESSAGE_BAR,
              MessageBarStore.actions.success,
              {
                title:
                  '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.TITEL_ALLE_ARTIKEL_ABGESCHLOSSEN',
                message:
                  '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.LABEL_ALLE_ARTIKEL_ABGESCHLOSSEN' }
            );
            await this.router.navigate(['/llr-bskomm/warenzugang-abschliessen']);
          } else {
            await this.router.navigate(
              [`llr-bskomm/bewegung/${bewegung.id}/emballagen-erfassung`],
              {
                queryParams: { paletteId: this.palette()?.id ?? '' } }
            );
          }
        } catch (e) {
          showRequestErrors(this.store, e);
        }
      }
    }
  }

  async kommissionierungSchreiben(
    bewegung: KommissionierungBewegung
  ): Promise<KommissionierungBewegung | null> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.kommissionierungSchreiben,
        {
          bewe: bewegung,
          params: {
            idkopfbskomm: this.kommissionierung?.id + '',
            idbewebskomm: bewegung.id + '',
            idzielpalette: this.palette() ? this.palette()?.id + '' : '' } }
      );
      if (!result) return null;

      return result;
    } catch (e) {
      showRequestErrors(this.store, e);
      return null;
    }
  }

  async beweAbschliessen(
    bewegung: KommissionierungBewegung
  ): Promise<Kommissionierung | undefined> {
    try {
      if (bewegung.kommissionierDaten.menge === 0) return;

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.loading
      );
      this.canExecuteWeiterButton$.next(false);

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.artikelpositionAbschliessen,
        {
          idkopfbskomm: bewegung.idkopfbskomm + '',
          idbewebskomm: bewegung.id + '',
          bewe: bewegung }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.closeAll
      );
      this.canExecuteWeiterButton$.next(true);

      return result;
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.closeAll
      );
      showRequestErrors(this.store, e);
      this.canExecuteWeiterButton$.next(true);
      return undefined;
    }
  }
}
